import { useCallback, useRef, useState } from "react";
import { toast } from "react-toastify";
import OngoingRoomConflictModal from "./OngoingRoomConflictModal";
import StatusConfirmModal, {
  type ConfirmableStatus,
} from "./StatusConfirmModal";
import {
  fetchOtherOngoingInSameRoom,
  normalizeRoom,
  OngoingConflictAppointment,
  resolveOngoingConflicts,
  type OngoingResolution,
} from "./ongoingRoomGuard";
import { patientNameFromAppointment } from "../../../../utils/dispatchTokenPrint";

type GateItem = {
  _id?: string;
  date?: string;
  roomNumber?: unknown;
  patientId?: { fullName?: string };
  bookPatientId?: { fullName?: string };
};

const CONFIRMABLE = new Set<string>([
  "waiting",
  "in_queue",
  "ongoing",
  "completed",
  "complete",
]);

function isConfirmableStatus(status: string): status is ConfirmableStatus {
  return CONFIRMABLE.has(status);
}

/**
 * Confirm status changes (on hold / ongoing / completed) with a modal.
 * For ongoing, also require same-room ongoing patients to be
 * completed or put on hold before allowing the status change.
 */
export function useOngoingStatusGate() {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmStatus, setConfirmStatus] = useState<ConfirmableStatus | null>(
    null
  );
  const [confirmPatientName, setConfirmPatientName] = useState("");
  const [others, setOthers] = useState<OngoingConflictAppointment[]>([]);
  const [roomNumber, setRoomNumber] = useState("");
  const [conflictOpen, setConflictOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const resolveRef = useRef<((ok: boolean) => void) | null>(null);
  const pendingItemRef = useRef<GateItem | null>(null);

  const finish = useCallback((ok: boolean) => {
    setConfirmOpen(false);
    setConfirmStatus(null);
    setConfirmPatientName("");
    setConflictOpen(false);
    setOthers([]);
    setSaving(false);
    pendingItemRef.current = null;
    resolveRef.current?.(ok);
    resolveRef.current = null;
  }, []);

  const continueAfterConfirm = useCallback(
    async (item: GateItem, status: ConfirmableStatus) => {
      if (status !== "ongoing") {
        finish(true);
        return;
      }

      const room = normalizeRoom(item?.roomNumber);
      if (!room) {
        finish(true);
        return;
      }

      try {
        const conflicts = await fetchOtherOngoingInSameRoom(item);
        if (conflicts.length === 0) {
          finish(true);
          return;
        }
        setConfirmOpen(false);
        setRoomNumber(room);
        setOthers(conflicts);
        setConflictOpen(true);
      } catch (err) {
        console.error(err);
        toast.error("Could not check other ongoing appointments in this room.");
        finish(false);
      }
    },
    [finish]
  );

  const gateStatus = useCallback(
    async (item: GateItem, status: string): Promise<boolean> => {
      if (!isConfirmableStatus(status)) return true;

      return await new Promise<boolean>((resolve) => {
        resolveRef.current = resolve;
        pendingItemRef.current = item;
        setConfirmStatus(status);
        setConfirmPatientName(patientNameFromAppointment(item) || "");
        setConfirmOpen(true);
      });
    },
    []
  );

  /** @deprecated Prefer gateStatus(item, "ongoing") */
  const gateOngoing = useCallback(
    async (item: GateItem): Promise<boolean> => gateStatus(item, "ongoing"),
    [gateStatus]
  );

  const handleConfirmStatus = useCallback(() => {
    const item = pendingItemRef.current;
    const status = confirmStatus;
    if (!item || !status) {
      finish(false);
      return;
    }
    void continueAfterConfirm(item, status);
  }, [confirmStatus, continueAfterConfirm, finish]);

  const handleConflictConfirm = useCallback(
    async (
      resolutions: Array<{
        appointment: OngoingConflictAppointment;
        status: OngoingResolution;
      }>
    ) => {
      setSaving(true);
      try {
        await resolveOngoingConflicts(resolutions);
        finish(true);
      } catch (err) {
        console.error(err);
        toast.error("Could not update the other ongoing appointment(s).");
        setSaving(false);
      }
    },
    [finish]
  );

  const ongoingGateModal = (
    <>
      <StatusConfirmModal
        open={confirmOpen}
        status={confirmStatus}
        patientName={confirmPatientName || undefined}
        onCancel={() => finish(false)}
        onConfirm={handleConfirmStatus}
      />
      <OngoingRoomConflictModal
        open={conflictOpen}
        roomNumber={roomNumber}
        others={others}
        saving={saving}
        onCancel={() => finish(false)}
        onConfirm={handleConflictConfirm}
      />
    </>
  );

  return { gateStatus, gateOngoing, ongoingGateModal };
}
